import { useState, useEffect, useRef } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [heroVisible, setHeroVisible] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHeroVisible(true);
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      const sections = ['home', 'services', 'industries', 'case-studies', 'about'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const dark = '#0E1A2B';
  const light = '#F0F4F8';

  // Theme tokens
  const t = {
    pageBg: isDark ? 'bg-[#0E1A2B]' : 'bg-[#F0F4F8]',
    sectionAlt: isDark ? 'bg-[#0a1220]' : 'bg-[#E8EEF5]',
    sectionDeep: isDark ? 'bg-[#060e1a]' : 'bg-[#DDE5EF]',
    navBg: scrolled
      ? isDark
        ? 'bg-[#0E1A2B]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl'
        : 'bg-[#0E1A2B]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl'
      : 'bg-transparent',
    navLink: 'text-white/70 hover:text-white',
    navLinkActive: 'text-[#2ED6C3]',
    headingColor: isDark ? 'text-white' : 'text-[#0E1A2B]',
    subText: isDark ? 'text-white/60' : 'text-[#0E1A2B]/60',
    bodyText: isDark ? 'text-white/70' : 'text-[#0E1A2B]/70',
    cardBg: isDark
      ? 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
      : 'bg-white border border-black/10 hover:bg-gray-50 hover:border-black/20',
    statsBg: isDark ? 'bg-[#0E1A2B] border-y border-white/10' : 'bg-white border-y border-black/10',
    statsDivide: isDark ? 'divide-x divide-white/10' : 'divide-x divide-black/10',
    footerBg: isDark ? 'bg-[#060e1a] border-t border-white/10' : 'bg-[#E8EEF5] border-t border-black/10',
    footerText: isDark ? 'text-white/50' : 'text-[#0E1A2B]/60',
    footerCopy: isDark ? 'text-white/40' : 'text-[#0E1A2B]/50',
    footerDivide: isDark ? 'border-white/10' : 'border-black/10',
    footerIcon: isDark ? 'bg-white/10 text-white/50' : 'bg-[#0E1A2B]/10 text-[#0E1A2B]/60',
    // Hero overlay — dark enough for text contrast in both modes
    overlayHero: isDark
      ? 'bg-gradient-to-b from-[#0E1A2B]/60 via-[#0E1A2B]/40 to-[#0E1A2B]'
      : 'bg-gradient-to-b from-[#0E1A2B]/55 via-[#0E1A2B]/35 to-[#F0F4F8]',
    toggleBg: isDark
      ? 'bg-white/10 border border-white/20'
      : 'bg-[#0E1A2B]/10 border border-[#0E1A2B]/20',
    toggleIcon: isDark ? 'text-[#F4B942]' : 'text-[#0E1A2B]',
    // Image card overlays — always dark so white text stays readable
    cardOverlayR: 'bg-gradient-to-r from-[#060e1a]/90 via-[#060e1a]/55 to-transparent',
    cardOverlayT: 'bg-gradient-to-t from-[#060e1a]/95 via-[#060e1a]/50 to-transparent',
    // Services image overlay adapts slightly
    servicesImgOverlay: isDark
      ? 'bg-gradient-to-l from-transparent to-[#0E1A2B]/30'
      : 'bg-gradient-to-l from-transparent to-[#0E1A2B]/20',
    // CTA section always dark (has bg image)
    ctaBg: isDark ? 'bg-[#0E1A2B]/80' : 'bg-[#0E1A2B]/70',
    // Stat label
    statLabel: isDark ? 'text-white/50' : 'text-[#0E1A2B]/55',
    // Industries button
    industriesBtn: isDark
      ? 'bg-white/10 border-white/20 text-white hover:bg-[#2ED6C3] hover:border-[#2ED6C3] hover:text-[#0E1A2B]'
      : 'bg-[#0E1A2B]/10 border-[#0E1A2B]/20 text-[#0E1A2B] hover:bg-[#2ED6C3] hover:border-[#2ED6C3] hover:text-[#0E1A2B]',
    // Scroll indicator
    scrollText: isDark ? 'text-white/50' : 'text-[#0E1A2B]/50',
    scrollLine: isDark ? 'from-white/50' : 'from-[#0E1A2B]/50',
    // Big number watermark on service cards
    cardWatermark: isDark ? 'text-white' : 'text-[#0E1A2B]',
  };

  return (
    <div className={`min-h-screen ${t.pageBg} font-['Inter',sans-serif] transition-colors duration-500`}>

      {/* ── Navigation ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${t.navBg}`}>
        <div className="mx-auto px-16 py-5 flex items-center justify-between">
          <button onClick={() => scrollToSection('home')} className="cursor-pointer flex items-center">
            <img
              src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/9a7f5c456fe7707af82c950ac9d0e482.png"
              alt="CrossOver AI"
              className="h-28 w-28 object-contain"
            />
          </button>

          <div className="flex items-center gap-10">
            {[
              { id: 'home', label: 'Home' },
              { id: 'services', label: 'Services' },
              { id: 'industries', label: 'Industries' },
              { id: 'case-studies', label: 'Case Studies' },
              { id: 'about', label: 'About' },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                  activeSection === item.id ? t.navLinkActive : t.navLink
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsDark(!isDark)}
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${t.toggleBg}`}
              aria-label="Toggle dark/light mode"
            >
              <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} text-lg ${t.toggleIcon}`}></i>
            </button>

            <button
              onClick={() => scrollToSection('about')}
              className="px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap bg-[#2F6BFF] text-white hover:bg-[#2ED6C3] hover:text-[#0E1A2B]"
            >
              Schedule a Call
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section id="home" ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e5a36d45f2fddd4babc0d2ae8b023aad.jpg"
            alt="CrossOver AI Hero"
            className="w-full h-full object-cover object-center"
          />
          <div className={`absolute inset-0 ${t.overlayHero}`}></div>
        </div>

        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #2F6BFF 1px, transparent 1px)', backgroundSize: '44px 44px' }}></div>

        <div className={`relative z-10 text-center px-8 max-w-5xl mx-auto w-full transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#2ED6C3]/40 bg-[#2ED6C3]/10 mb-10">
            <span className="w-2 h-2 rounded-full bg-[#2ED6C3] animate-pulse"></span>
            <span className="text-[#2ED6C3] text-xs font-semibold tracking-widest uppercase">AI Automation Agency</span>
          </div>

          {/* Hero text always white — sits on dark image overlay */}
          <h1 className="text-[80px] font-black text-white leading-[1.05] mb-8 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Where Business<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2F6BFF] to-[#2ED6C3]">Crosses Into</span><br />
            Intelligence
          </h1>

          <p className="text-xl text-white/70 leading-relaxed max-w-2xl mx-auto mb-14">
            We help professional service firms and operational businesses implement AI automation that improves efficiency, response time, and scalability.
          </p>

          <div className="flex items-center justify-center gap-5">
            <button
              onClick={() => scrollToSection('services')}
              className="px-10 py-4 bg-[#2F6BFF] text-white text-base font-semibold rounded-full hover:shadow-2xl hover:shadow-[#2F6BFF]/40 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Start Your AI Transition
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="px-10 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white text-base font-semibold rounded-full hover:bg-white/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Explore Solutions
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className={`${t.scrollText} text-xs tracking-widest uppercase`}>Scroll</span>
          <div className={`w-px h-12 bg-gradient-to-b ${t.scrollLine} to-transparent`}></div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className={`${t.statsBg} py-12 transition-colors duration-500`}>
        <div className={`max-w-[1400px] mx-auto px-16 grid grid-cols-4 gap-0 ${t.statsDivide}`}>
          {[
            { value: '3x', label: 'Faster Response Times' },
            { value: '60%', label: 'Reduction in Admin Work' },
            { value: '2.5x', label: 'More Bookings' },
            { value: '85%', label: 'Lead Capture Rate' },
          ].map((stat, i) => (
            <div key={i} className="text-center px-10">
              <div className="text-5xl font-black text-[#2ED6C3] mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>{stat.value}</div>
              <div className={`text-sm ${t.statLabel} tracking-wide`}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Feature Grid ── */}
      <section className={`${t.sectionAlt} py-32 transition-colors duration-500`}>
        <div className="max-w-[1400px] mx-auto px-16">
          <div className="flex flex-col items-center text-center mb-20">
            <p className="text-[#2ED6C3] text-sm font-semibold tracking-widest uppercase mb-4">Core Capabilities</p>
            <h2 className={`text-6xl font-black ${t.headingColor}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Built for Transformation
            </h2>
          </div>

          {/* Large feature card — always dark overlay so white text is readable */}
          <div className="rounded-3xl overflow-hidden mb-6 relative h-[560px] group cursor-pointer">
            <img
              src="https://readdy.ai/api/search-image?query=ultra-wide%20cinematic%20shot%20of%20glowing%20neural%20network%20pathways%20flowing%20through%20a%20dark%20navy%20space%2C%20electric%20blue%20and%20teal%20data%20streams%20forming%20complex%20automated%20workflow%20diagrams%2C%20abstract%20AI%20intelligence%20visualization%2C%20photorealistic%208k%20render%2C%20deep%20space%20atmosphere%20with%20luminous%20connection%20nodes%2C%20no%20text%2C%20clean%20minimal%20futuristic%20aesthetic&width=1400&height=560&seq=feature-main-v2&orientation=landscape"
              alt="AI Workflow Automation"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className={`absolute inset-0 ${t.cardOverlayR}`}></div>
            <div className="absolute inset-0 flex flex-col justify-center px-20">
              <div className="inline-block px-4 py-1.5 bg-[#F4B942] text-[#0E1A2B] text-xs font-bold rounded-full mb-6 w-fit uppercase tracking-wider">Automation</div>
              <h3 className="text-5xl font-black text-white mb-5 max-w-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>AI Workflow<br />Automation</h3>
              <p className="text-white/75 text-lg max-w-md leading-relaxed">Automate intake, scheduling, internal coordination, and administrative processes end-to-end.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {[
              {
                label: 'Intelligence',
                title: 'AI Communication Systems',
                desc: 'AI systems capture leads, respond to inquiries, and schedule appointments automatically.',
                image: 'https://readdy.ai/api/search-image?query=close-up%20of%20glowing%20teal%20and%20blue%20holographic%20communication%20interface%20floating%20in%20dark%20space%2C%20AI%20chat%20and%20voice%20nodes%20interconnected%20with%20luminous%20threads%2C%20abstract%20digital%20communication%20network%20visualization%2C%20photorealistic%208k%2C%20deep%20navy%20background%20with%20electric%20accent%20colors%2C%20no%20text%2C%20minimal%20futuristic%20design&width=700&height=420&seq=feature-comm-v2&orientation=landscape',
                color: '#2ED6C3'
              },
              {
                label: 'Insights',
                title: 'AI Operational Intelligence',
                desc: 'Dashboards provide real-time visibility into response times, lead flow, and workflow performance.',
                image: 'https://readdy.ai/api/search-image?query=sleek%20dark%20dashboard%20interface%20with%20glowing%20real-time%20analytics%20charts%20and%20KPI%20metrics%2C%20deep%20navy%20background%20with%20electric%20blue%20and%20teal%20data%20visualizations%2C%20holographic%20business%20intelligence%20display%2C%20photorealistic%208k%20render%2C%20abstract%20operational%20insights%20with%20flowing%20data%20streams%2C%20no%20text%2C%20minimal%20enterprise%20aesthetic&width=700&height=420&seq=feature-ops-v2&orientation=landscape',
                color: '#2F6BFF'
              }
            ].map((card, i) => (
              <div key={i} className="rounded-3xl overflow-hidden relative h-[420px] group cursor-pointer">
                <img src={card.image} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className={`absolute inset-0 ${t.cardOverlayT}`}></div>
                <div className="absolute bottom-0 left-0 right-0 p-10">
                  <div className="inline-block px-4 py-1.5 text-xs font-bold rounded-full mb-4 uppercase tracking-wider" style={{ backgroundColor: `${card.color}30`, color: card.color }}>{card.label}</div>
                  <h3 className="text-3xl font-black text-white mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>{card.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section id="services" className={`py-32 ${t.pageBg} transition-colors duration-500`}>
        <div className="max-w-[1400px] mx-auto px-16">
          <div className="flex flex-col items-center text-center mb-24">
            <p className="text-[#2ED6C3] text-sm font-semibold tracking-widest uppercase mb-5">Services</p>
            <h2 className={`text-6xl font-black ${t.headingColor} leading-tight mb-8`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
              AI Systems That<br />Transform Operations
            </h2>
            <p className={`${t.bodyText} text-lg leading-relaxed max-w-2xl`}>
              We design and deploy automation systems that streamline workflows, improve communication, and unlock scalable growth.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {[
              { icon: 'ri-flow-chart', title: 'AI Workflow Automation', desc: 'Automate intake, scheduling, internal coordination, and administrative processes.', color: '#2F6BFF', num: '01' },
              { icon: 'ri-message-3-line', title: 'AI Client Communication', desc: 'AI systems capture leads, respond to inquiries, and schedule appointments automatically.', color: '#2ED6C3', num: '02' },
              { icon: 'ri-money-dollar-circle-line', title: 'AI Revenue Automation', desc: 'Automated follow-up and nurture systems help convert more prospects into clients.', color: '#F4B942', num: '03' },
              { icon: 'ri-dashboard-line', title: 'AI Operational Intelligence', desc: 'Dashboards provide visibility into response times, lead flow, and workflow performance.', color: '#2F6BFF', num: '04' },
            ].map((s, i) => (
              <div key={i} className={`group ${t.cardBg} rounded-3xl p-10 transition-all duration-500 cursor-pointer relative overflow-hidden`}>
                <div className={`absolute top-8 right-8 text-7xl font-black opacity-[0.06] ${t.cardWatermark}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{s.num}</div>
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl mb-8" style={{ backgroundColor: `${s.color}20` }}>
                  <i className={`${s.icon} text-2xl`} style={{ color: s.color }}></i>
                </div>
                <h3 className={`text-2xl font-bold ${t.headingColor} mb-4`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{s.title}</h3>
                <p className={`${t.subText} text-base leading-relaxed`}>{s.desc}</p>
                <div className="mt-8 flex items-center gap-2 text-sm font-semibold" style={{ color: s.color }}>
                  <span>Learn more</span>
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => scrollToSection('about')}
              className="px-12 py-4 bg-[#2F6BFF] text-white text-base font-semibold rounded-full hover:bg-[#2ED6C3] hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-[#2F6BFF]/30"
            >
              Build Your AI Infrastructure
            </button>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section id="industries" className={`${t.sectionDeep} py-32 transition-colors duration-500`}>
        <div className="max-w-[1400px] mx-auto px-16">
          <div className="flex flex-col items-center text-center mb-20">
            <p className="text-[#2ED6C3] text-sm font-semibold tracking-widest uppercase mb-5">Industries</p>
            <h2 className={`text-6xl font-black ${t.headingColor} mb-6`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
              AI Solutions for<br />Operational Industries
            </h2>
            <p className={`${t.subText} text-lg max-w-2xl`}>
              Automation systems tailored to complex industries where efficiency and responsiveness matter.
            </p>
          </div>

          {/* Industry hero card — always dark overlay */}
          <div className="rounded-3xl overflow-hidden relative h-[500px] mb-6 group cursor-pointer">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20modern%20law%20firm%20office%20interior%20at%20night%2C%20floor-to-ceiling%20glass%20walls%20overlooking%20a%20glowing%20city%20skyline%2C%20sleek%20dark%20furniture%20with%20subtle%20blue%20ambient%20lighting%2C%20holographic%20AI%20interface%20panels%20floating%20above%20desks%2C%20photorealistic%208k%2C%20deep%20navy%20and%20teal%20color%20palette%2C%20no%20people%2C%20ultra-clean%20minimal%20luxury%20atmosphere&width=1400&height=500&seq=industry-law-hero-v2&orientation=landscape"
              alt="Professional Services"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className={`absolute inset-0 ${t.cardOverlayR}`}></div>
            <div className="absolute inset-0 flex flex-col justify-center px-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#2ED6C3]/20">
                  <i className="ri-briefcase-line text-xl text-[#2ED6C3]"></i>
                </div>
                <span className="text-[#2ED6C3] text-sm font-semibold tracking-widest uppercase">Professional Services</span>
              </div>
              <h3 className="text-5xl font-black text-white mb-5 max-w-xl" style={{ fontFamily: 'Montserrat, sans-serif' }}>Law Firms &amp;<br />Insurance Agencies</h3>
              <p className="text-white/70 text-lg max-w-md leading-relaxed">Automate client intake, communication, and workflow coordination to serve more clients with less overhead.</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-heart-pulse-line',
                label: 'Healthcare',
                title: 'Medical Practices',
                desc: 'AI reduces administrative burden and improves scheduling and patient communication.',
                image: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20reception%20area%20at%20night%20with%20soft%20blue%20ambient%20lighting%2C%20clean%20white%20and%20navy%20interior%20design%2C%20holographic%20patient%20scheduling%20interface%20on%20wall%20display%2C%20photorealistic%208k%2C%20minimal%20healthcare%20environment%20with%20teal%20accent%20lighting%2C%20no%20people%2C%20ultra-clean%20luxury%20medical%20aesthetic&width=500&height=380&seq=industry-health-v2&orientation=portrait',
                color: '#2ED6C3'
              },
              {
                icon: 'ri-home-gear-line',
                label: 'Home Services',
                title: 'Contractors &amp; Trades',
                desc: 'Capture leads, schedule jobs, and automate follow-up to grow your service business.',
                image: 'https://readdy.ai/api/search-image?query=professional%20home%20services%20operations%20center%20with%20digital%20job%20scheduling%20boards%20and%20lead%20management%20screens%2C%20dark%20navy%20office%20environment%20with%20glowing%20blue%20and%20teal%20interface%20displays%2C%20photorealistic%208k%2C%20clean%20minimal%20workspace%20with%20automated%20workflow%20visualization%2C%20no%20people%2C%20modern%20enterprise%20aesthetic&width=500&height=380&seq=industry-home-v2&orientation=portrait',
                color: '#F4B942'
              },
              {
                icon: 'ri-building-line',
                label: 'Real Estate',
                title: 'Brokerages &amp; Agents',
                desc: 'Automate inquiry handling, lead routing, and client communication at scale.',
                image: 'https://readdy.ai/api/search-image?query=luxury%20real%20estate%20brokerage%20office%20interior%20at%20night%2C%20panoramic%20city%20view%20through%20floor-to-ceiling%20windows%2C%20sleek%20dark%20furniture%20with%20blue%20and%20gold%20accent%20lighting%2C%20holographic%20property%20listing%20interfaces%20floating%20in%20space%2C%20photorealistic%208k%2C%20no%20people%2C%20ultra-premium%20minimal%20atmosphere&width=500&height=380&seq=industry-realestate-v2&orientation=portrait',
                color: '#2F6BFF'
              }
            ].map((ind, i) => (
              <div key={i} className="rounded-3xl overflow-hidden relative h-[380px] group cursor-pointer">
                <img src={ind.image} alt={ind.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className={`absolute inset-0 ${t.cardOverlayT}`}></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="inline-flex items-center gap-2 mb-4">
                    <i className={`${ind.icon} text-lg`} style={{ color: ind.color }}></i>
                    <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: ind.color }}>{ind.label}</span>
                  </div>
                  <h3 className="text-2xl font-black text-white mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }} dangerouslySetInnerHTML={{ __html: ind.title }}></h3>
                  <p className="text-white/70 text-sm leading-relaxed">{ind.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => scrollToSection('about')}
              className={`px-12 py-4 border text-base font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${t.industriesBtn}`}
            >
              See How AI Can Transform Your Industry
            </button>
          </div>
        </div>
      </section>

      {/* ── Case Studies ── */}
      <section id="case-studies" className={`py-32 ${t.pageBg} transition-colors duration-500`}>
        <div className="max-w-[1400px] mx-auto px-16">
          <div className="flex flex-col items-center text-center mb-20">
            <p className="text-[#2ED6C3] text-sm font-semibold tracking-widest uppercase mb-5">Client Results</p>
            <h2 className={`text-6xl font-black ${t.headingColor} leading-tight mb-6`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Operational<br />Transformation<br />Through AI
            </h2>
            <p className={`${t.bodyText} text-lg leading-relaxed max-w-2xl`}>
              Organizations across multiple industries are transitioning into intelligent operations. Here are the results.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {[
              { industry: 'Law Firm', challenge: 'Slow inquiry response and manual intake', solution: 'AI lead capture, automated communication, and workflow automation', result: 'Faster response times and improved conversion', metric: '3x', metricLabel: 'Response Speed', color: '#2F6BFF' },
              { industry: 'Healthcare Practice', challenge: 'Administrative overload and scheduling conflicts', solution: 'AI scheduling system and patient communication automation', result: 'Improved scheduling and reduced administrative workload', metric: '60%', metricLabel: 'Time Saved', color: '#2ED6C3' },
              { industry: 'Home Services', challenge: 'Missed leads and inconsistent follow-up', solution: 'AI lead capture and automated follow-up sequences', result: 'Increased bookings and automated follow-up', metric: '2.5x', metricLabel: 'More Bookings', color: '#F4B942' },
              { industry: 'Insurance Agency', challenge: 'Manual quote processing and slow client response', solution: 'AI-powered quote automation and instant client communication', result: 'Streamlined operations and faster client service', metric: '4x', metricLabel: 'Quote Speed', color: '#2F6BFF' },
              { industry: 'Real Estate Brokerage', challenge: 'Lead routing delays and missed opportunities', solution: 'AI inquiry handling and intelligent lead distribution', result: 'Optimized lead flow and improved agent productivity', metric: '85%', metricLabel: 'Lead Capture', color: '#2ED6C3' },
              { industry: 'Consulting Firm', challenge: 'Time-consuming client onboarding process', solution: 'AI workflow automation and document processing', result: 'Accelerated onboarding and enhanced client experience', metric: '70%', metricLabel: 'Faster Onboarding', color: '#F4B942' },
            ].map((study, i) => (
              <div key={i} className={`group ${t.cardBg} rounded-3xl p-8 transition-all duration-500 cursor-pointer relative overflow-hidden`}>
                <div className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl" style={{ backgroundColor: study.color }}></div>
                <div className="mb-6 pt-2">
                  <div className="text-5xl font-black mb-1" style={{ color: study.color, fontFamily: 'Montserrat, sans-serif' }}>{study.metric}</div>
                  <div className={`text-xs font-semibold ${t.subText} uppercase tracking-widest`}>{study.metricLabel}</div>
                </div>
                <h3 className={`text-lg font-bold ${t.headingColor} mb-5`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{study.industry}</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Challenge', text: study.challenge, color: '#2F6BFF' },
                    { label: 'Solution', text: study.solution, color: '#2ED6C3' },
                    { label: 'Result', text: study.result, color: '#F4B942' },
                  ].map((item, j) => (
                    <div key={j}>
                      <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: item.color }}>{item.label}</div>
                      <p className={`${t.subText} text-sm leading-relaxed`}>{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className={`text-2xl font-bold ${t.headingColor} mb-6`} style={{ fontFamily: 'Montserrat, sans-serif' }}>Your organization could be next.</p>
            <button
              onClick={() => scrollToSection('about')}
              className="px-12 py-4 bg-[#2F6BFF] text-white text-base font-semibold rounded-full hover:bg-[#2ED6C3] hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-[#2F6BFF]/30"
            >
              Schedule a Strategy Session
            </button>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className={`${t.sectionDeep} py-32 overflow-hidden transition-colors duration-500`}>
        <div className="max-w-[1400px] mx-auto px-16">
          <div className="relative rounded-3xl overflow-hidden h-[480px] mb-24">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20glowing%20bridge%20of%20light%20crossing%20a%20vast%20dark%20chasm%2C%20luminous%20pathway%20made%20of%20electric%20blue%20and%20teal%20data%20streams%2C%20golden%20nodes%20marking%20the%20crossing%20points%2C%20deep%20midnight%20navy%20atmosphere%2C%20photorealistic%208k%20render%2C%20abstract%20metaphor%20for%20business%20transformation%20and%20AI%20transition%2C%20no%20people%2C%20ultra-minimal%20futuristic%20aesthetic&width=1400&height=480&seq=about-bridge-v2&orientation=landscape"
              alt="About CrossOver AI"
              className="w-full h-full object-cover"
            />
            <div className={`absolute inset-0 ${t.cardOverlayR}`}></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-20">
              <p className="text-[#2ED6C3] text-sm font-semibold tracking-widest uppercase mb-6">About</p>
              <h2 className="text-6xl font-black text-white leading-tight max-w-2xl" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Guiding Businesses<br />Through the AI Transition
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-24 mb-24">
            <div className="space-y-6">
              <p className={`text-xl ${t.bodyText} leading-relaxed`}>
                Many organizations understand the promise of artificial intelligence but struggle to implement it effectively.
              </p>
              <p className={`text-xl ${t.bodyText} leading-relaxed`}>
                We help businesses move from outdated workflows into modern, automated operational systems — practical AI solutions that streamline communication, automate workflows, and unlock scalable growth.
              </p>
            </div>
            <div className="space-y-6">
              <p className={`text-xl ${t.bodyText} leading-relaxed`}>
                Our approach is rooted in operational reality. We don&apos;t sell technology for its own sake — we build systems that solve real problems and deliver measurable results.
              </p>
              <p className={`text-xl ${t.bodyText} leading-relaxed`}>
                Every engagement begins with understanding your operations, then designing automation that fits your business — not the other way around.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-6">
            {[
              { icon: 'ri-lightbulb-line', title: 'Clarity Over Complexity', desc: 'We make AI simple, practical, and immediately useful.', color: '#2F6BFF' },
              { icon: 'ri-brain-line', title: 'Practical Intelligence', desc: 'Every system we build solves a real operational problem.', color: '#2ED6C3' },
              { icon: 'ri-medal-line', title: 'Operational Excellence', desc: 'We hold ourselves to the same standards we set for your systems.', color: '#F4B942' },
              { icon: 'ri-line-chart-line', title: 'Long-Term Transformation', desc: 'We build for lasting change, not short-term fixes.', color: '#2F6BFF' },
            ].map((p, i) => (
              <div key={i} className={`${t.cardBg} rounded-3xl p-8 transition-all duration-300 cursor-pointer group`}>
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: `${p.color}20` }}>
                  <i className={`${p.icon} text-xl`} style={{ color: p.color }}></i>
                </div>
                <h3 className={`text-lg font-bold ${t.headingColor} mb-3`} style={{ fontFamily: 'Montserrat, sans-serif' }}>{p.title}</h3>
                <p className={`${t.subText} text-sm leading-relaxed`}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── always dark (full bg image) ── */}
      <section className="relative py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dramatic%20overhead%20aerial%20view%20of%20glowing%20crossroads%20intersection%20at%20night%2C%20massive%20luminous%20pathways%20of%20electric%20blue%20and%20teal%20light%20converging%20from%20all%20directions%2C%20golden%20intersection%20nodes%2C%20deep%20midnight%20navy%20sky%2C%20photorealistic%208k%2C%20ultra-wide%20cinematic%20perspective%2C%20abstract%20data%20highway%20visualization%2C%20no%20people%2C%20no%20text%2C%20clean%20minimal%20futuristic%20atmosphere&width=1920&height=700&seq=cta-bg-v3&orientation=landscape"
            alt="CTA Background"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${t.ctaBg}`}></div>
        </div>
        <div className="relative z-10 text-center px-8 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-8">
            <i className="ri-arrow-right-line text-xl text-[#F4B942]"></i>
            <i className="ri-arrow-right-line text-xl text-[#F4B942] opacity-60"></i>
            <i className="ri-arrow-right-line text-xl text-[#F4B942] opacity-30"></i>
          </div>
          <h2 className="text-6xl font-black text-white mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to cross into<br />intelligent operations?
          </h2>
          <p className="text-white/65 text-xl mb-12 max-w-xl mx-auto">
            Schedule a strategy call and discover how AI automation can transform your business.
          </p>
          <button
            onClick={() => scrollToSection('about')}
            className="px-14 py-5 bg-[#2ED6C3] text-[#0E1A2B] text-lg font-bold rounded-full hover:bg-[#F4B942] hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-2xl shadow-[#2ED6C3]/30"
          >
            Schedule a Strategy Call
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={`${t.footerBg} transition-colors duration-500`}>
        <div className="max-w-[1400px] mx-auto px-16 py-20">
          <div className="flex flex-col items-center text-center mb-16">
            <img
              src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/9a7f5c456fe7707af82c950ac9d0e482.png"
              alt="CrossOver AI"
              className="h-28 w-28 object-contain mb-4"
            />
            <p className={`text-sm ${t.footerText} leading-relaxed`}>Intelligent Operations for Modern Business</p>
            <div className="flex items-center gap-4 mt-6">
              {['ri-linkedin-fill', 'ri-twitter-x-fill', 'ri-facebook-fill'].map((icon, i) => (
                <a key={i} href="#" rel="nofollow" className={`w-9 h-9 flex items-center justify-center rounded-full ${t.footerIcon} hover:bg-[#2F6BFF] hover:text-white transition-all duration-300 cursor-pointer`}>
                  <i className={`${icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          <div className={`flex items-center justify-between pt-8 border-t ${t.footerDivide}`}>
            <p className={`text-sm ${t.footerCopy}`}>© 2026 CrossOver AI. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="#" rel="nofollow" className={`text-sm ${t.footerCopy} hover:text-[#2F6BFF] transition-colors cursor-pointer`}>Privacy Policy</a>
              <a href="#" rel="nofollow" className={`text-sm ${t.footerCopy} hover:text-[#2F6BFF] transition-colors cursor-pointer`}>Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
